import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Button, buttonVariants, Input, Label, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Textarea } from '@cuspar/ui';
import { IPurchaseDomainType } from '@cuspar/ywt.ch.model';
import { useCartStore } from '../../state/cartStore';
import { domainAPI } from '../../state/api/domain';
import { ROUTES } from '../../routes';
import { PriceGadget } from '../UI/PriceGadget';
import { BATCH_CHECK_TIMEOUT_MS, batchRowError, MAX_BATCH_BYTES, MAX_BATCH_TRANSFERS, normalizeTransferDomain, parseBatchTransfers, type BatchTransferRow } from './batchTransfer.logic';

type RowStatus = 'checking' | 'ready' | 'unavailable' | 'checkFailed' | 'adding' | 'added' | 'addFailed';

export function BatchTransfer({ onBusyChange }: { onBusyChange: (busy: boolean) => void }) {
  const { t } = useTranslation();
  const cart = useCartStore((state) => state.cart);
  const prices = useCartStore((state) => state.prices);
  const [source, setSource] = useState('');
  const [rows, setRows] = useState<BatchTransferRow[]>([]);
  const [statuses, setStatuses] = useState<Record<number, RowStatus>>({});
  // Server detail behind a failed or unavailable row, e.g. "Invalid auth code" or a throttle message.
  const [reasons, setReasons] = useState<Record<number, string>>({});
  const [inputError, setInputError] = useState('');
  const [busy, setBusy] = useState(false);
  const locked = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const cartDomains = cart?.domains.map((item) => item.domain) ?? [];
  const errorFor = (row: BatchTransferRow) => batchRowError(row, rows, cartDomains);
  const ready = rows.filter((row) => !errorFor(row) && ['ready', 'addFailed'].includes(statuses[row.id]));
  const checkable = rows.filter((row) => !errorFor(row) && !['ready', 'addFailed'].includes(statuses[row.id]));
  const priceFor = (row: BatchTransferRow, type: IPurchaseDomainType) =>
    prices.find((price) => price.tld === normalizeTransferDomain(row.domain).split('.').pop() && price.type === type && price.product === 'domain');

  function preview(text: string) {
    const result = parseBatchTransfers(text);
    if (result.error) {
      setInputError(t(`domain.batch.${result.error}`, { count: MAX_BATCH_TRANSFERS }));
      return;
    }
    setRows(result.rows!);
    setStatuses({});
    setReasons({});
    setSource('');
    setInputError('');
  }

  function setResult(id: number, status?: RowStatus, reason?: string) {
    setStatuses((current) => {
      const next = { ...current };
      if (status) next[id] = status;
      else delete next[id];
      return next;
    });
    setReasons((current) => {
      const next = { ...current };
      if (reason) next[id] = reason;
      else delete next[id];
      return next;
    });
  }

  function updateRow(id: number, change: Partial<BatchTransferRow>) {
    setRows((current) => current.map((row) => (row.id === id ? { ...row, ...change } : row)));
    setResult(id);
  }

  async function run(action: 'check' | 'add') {
    if (locked.current) return;
    locked.current = true;
    setBusy(true);
    onBusyChange(true);
    try {
      // Cart writes are deliberately sequential: concurrent saves can lose earlier additions.
      for (const row of action === 'check' ? checkable : ready) {
        if (!mounted.current) break;
        const domain = normalizeTransferDomain(row.domain);
        if (batchRowError(row, rows, useCartStore.getState().cart?.domains.map((item) => item.domain) ?? [])) continue;
        setResult(row.id, action === 'check' ? 'checking' : 'adding');
        let status: RowStatus;
        let reason: string | undefined;
        try {
          if (action === 'check') {
            const response = await domainAPI.checkDomainTransferAvailability(domain, row.code.trim(), { quiet: true, timeoutMs: BATCH_CHECK_TIMEOUT_MS });
            if (!response.success) {
              status = 'checkFailed';
              reason = response.reason;
            } else {
              status = response.data.available ? 'ready' : 'unavailable';
              reason = response.data.reason;
            }
          } else {
            // Quiet: a failing tail (throttle, outage) must not toast once per row; the row shows the reason.
            const result = await useCartStore.getState().addItemToCart({ type: 'domain', item: { domain, type: IPurchaseDomainType.Transfer, transferAuthCode: row.code.trim(), price: 0 } }, true);
            status = result.success ? 'added' : 'addFailed';
            reason = result.success ? undefined : result.reason;
          }
        } catch {
          status = action === 'check' ? 'checkFailed' : 'addFailed';
        }
        if (mounted.current) setResult(row.id, status, reason);
      }
    } finally {
      locked.current = false;
      if (mounted.current) {
        setBusy(false);
        onBusyChange(false);
      }
    }
  }

  return (
    <div className='space-y-5'>
      <p className='text-sm text-muted-foreground'>{t('domain.batch.description', { count: MAX_BATCH_TRANSFERS })}</p>
      <p className='text-sm'>{t('domain.batch.nameservers')}</p>
      {rows.length === 0 ? (
        <div className='space-y-4'>
          <div className='space-y-2'>
            <Label htmlFor='batch-file'>{t('domain.batch.upload')}</Label>
            <Input
              id='batch-file'
              type='file'
              accept='.csv,.tsv,text/csv,text/tab-separated-values'
              disabled={busy}
              onChange={async (event) => {
                const file = event.target.files?.[0];
                event.target.value = '';
                if (!file) return;
                if (file.size > MAX_BATCH_BYTES) {
                  setInputError(t('domain.batch.tooLarge'));
                  return;
                }
                setBusy(true);
                try {
                  const text = await file.text();
                  if (mounted.current) preview(text);
                } catch {
                  if (mounted.current) setInputError(t('domain.batch.readFailed'));
                } finally {
                  if (mounted.current) setBusy(false);
                }
              }}
            />
          </div>
          <div className='space-y-2'>
            <Label htmlFor='batch-source'>{t('domain.batch.paste')}</Label>
            <Textarea
              id='batch-source'
              rows={6}
              value={source}
              disabled={busy}
              onChange={(event) => setSource(event.target.value)}
              placeholder={'domain,auth_code\nexample.ch,TRANSFER-CODE\nexample.li,TRANSFER-CODE'}
              autoComplete='off'
              autoCorrect='off'
              autoCapitalize='off'
              spellCheck={false}
              data-sentry-mask
            />
          </div>
          <p className='text-xs text-muted-foreground'>{t('domain.batch.format')}</p>
          <Button onClick={() => preview(source)} disabled={busy || !source.trim()}>
            {t('domain.batch.preview')}
          </Button>
        </div>
      ) : (
        <>
          <p className='text-sm'>{t('domain.batch.review', { count: rows.length })}</p>
          <div className='overflow-x-auto rounded border'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('common.domain')}</TableHead>
                  <TableHead>{t('common.code')}</TableHead>
                  <TableHead>{t('domain.batch.pricing')}</TableHead>
                  <TableHead>{t('common.status')}</TableHead>
                  <TableHead>
                    <span className='sr-only'>{t('domain.batch.remove')}</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row) => {
                  const inCart = cartDomains.includes(normalizeTransferDomain(row.domain));
                  const status = inCart && statuses[row.id] === 'added' ? 'added' : (errorFor(row) ?? (statuses[row.id] === 'added' ? 'unchecked' : statuses[row.id]) ?? 'unchecked');
                  const reason = status === statuses[row.id] ? reasons[row.id] : undefined;
                  const transferPrice = priceFor(row, IPurchaseDomainType.Transfer);
                  const renewalPrice = priceFor(row, IPurchaseDomainType.Renewal);
                  return (
                    <TableRow key={row.id}>
                      <TableCell>
                        <Input
                          aria-label={t('domain.batch.domainRow', { count: row.id + 1 })}
                          value={row.domain}
                          className='min-w-40'
                          disabled={busy || inCart}
                          onChange={(event) => updateRow(row.id, { domain: event.target.value })}
                          autoCapitalize='off'
                          autoCorrect='off'
                          spellCheck={false}
                        />
                      </TableCell>
                      <TableCell>
                        <Input
                          aria-label={t('domain.batch.codeRow', { count: row.id + 1 })}
                          type='password'
                          value={row.code}
                          className='min-w-36'
                          disabled={busy || inCart}
                          onChange={(event) => updateRow(row.id, { code: event.target.value })}
                          autoComplete='off'
                          data-sentry-mask
                        />
                      </TableCell>
                      <TableCell className='text-xs min-w-36'>
                        {transferPrice && renewalPrice ? (
                          <>
                            <div>
                              {t('domain.batch.transferPrice')} <PriceGadget price={transferPrice.price} currency={transferPrice.currency} />
                            </div>
                            <div>
                              {t('domain.batch.renewalPrice')} <PriceGadget price={renewalPrice.price} currency={renewalPrice.currency} />
                            </div>
                          </>
                        ) : (
                          t('domain.batch.checkoutPrice')
                        )}
                      </TableCell>
                      <TableCell className='min-w-48 text-sm'>
                        <div role='status'>
                          <span>{t(`domain.batch.${status}`)}</span>
                          {reason && <span className='block text-xs text-muted-foreground'>{reason}</span>}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Button
                          size='sm'
                          variant='ghost'
                          disabled={busy}
                          aria-label={t('domain.batch.removeRow', { count: row.id + 1 })}
                          onClick={() => setRows((current) => current.filter((item) => item.id !== row.id))}
                        >
                          {t('domain.batch.remove')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <div className='flex flex-wrap gap-3'>
            <Button variant='outline' disabled={busy || !checkable.length} onClick={() => run('check')}>
              {t('domain.batch.check', { count: checkable.length })}
            </Button>
            <Button disabled={busy || !ready.length} onClick={() => run('add')}>
              {t('domain.batch.add', { count: ready.length })}
            </Button>
            <Button
              variant='ghost'
              disabled={busy}
              onClick={() => {
                setRows([]);
                setStatuses({});
                setReasons({});
                setInputError('');
              }}
            >
              {t('domain.batch.startOver')}
            </Button>
          </div>
          <p className='text-xs text-muted-foreground'>{t('domain.batch.cartNotice')}</p>
        </>
      )}
      {inputError && (
        <p role='alert' className='text-sm text-destructive'>
          {inputError}
        </p>
      )}
      {busy && <p role='status'>{t('domain.batch.working')}</p>}
      {!!cart?.domains.length && (
        <Link
          to={ROUTES.checkout.cart}
          className={buttonVariants({ className: busy ? 'pointer-events-none opacity-50' : undefined })}
          onClick={(event) => {
            if (busy) event.preventDefault();
          }}
          aria-disabled={busy}
        >
          {t('domain.batch.checkout')}
        </Link>
      )}
    </div>
  );
}
